// The phone's visible area, for the full-screen surfaces (runner.css: .runner, the sign-in and first-run screens, the
// sheets and dialogs). On iOS the on-screen keyboard doesn't shrink the page: it covers it, and only
// window.visualViewport gets shorter (and may pan down by offsetTop). A surface fixed to the layout viewport then
// leaves its bottom, the command bar, behind the keyboard, or floating mid-screen once Safari pans. So the root
// carries the visual viewport's height and top as --kr-vh / --kr-vtop, and the surfaces take them. Chrome on Android
// resizes the layout viewport itself (index.html asks for interactive-widget=resizes-content), where the same numbers
// simply match the window. While she pinch-zooms (scale != 1) the variables are dropped: the page keeps its size and
// zooms as usual. `kr-kb` on the root says the keyboard is up (a text field has focus and the visible height dropped),
// so the bottom safe-area gap (the home indicator's, under the keyboard now) can go; `kr-tight` says what is left above
// it is a thin strip (a phone on its side).

const FIELD = "input:not([type=checkbox]):not([type=radio]):not([type=button]):not([type=submit]):not([type=file]):not([type=range]), textarea, select, [contenteditable=''], [contenteditable=true]";

export function isTextField(el: Element | null): el is HTMLElement {
  return !!el && typeof el.matches === "function" && el.matches(FIELD);
}

/** The visible height in this orientation with no keyboard: the tallest seen in it. */
function tallest(win: Window): { get: (h: number) => number } {
  const seen = new Map<string, number>();
  return {
    get(h: number) {
      // Not the viewport's own aspect: with the keyboard up a portrait page can be wider than tall.
      const legacy = (win as Window & { orientation?: number }).orientation;
      const s = win.screen;
      const key = s?.orientation?.type ?? (typeof legacy === "number" ? (Math.abs(legacy) === 90 ? "landscape" : "portrait") : `${s?.width}x${s?.height}`);
      const best = Math.max(seen.get(key) ?? 0, h);
      seen.set(key, best);
      return best;
    },
  };
}

export function watchViewport(win: Window = window): () => void {
  const vv = win.visualViewport;
  const root = win.document.documentElement;
  const max = tallest(win);
  let frame = 0;
  let wasKb = false;
  let lastKey = "";
  let lastField: Element | null = null;
  const apply = () => {
    frame = 0;
    if (!vv) return;
    if (Math.abs(vv.scale - 1) > 0.01) {
      root.style.removeProperty("--kr-vh");
      root.style.removeProperty("--kr-vtop");
      return;
    }
    const height = Math.round(vv.height);
    const top = Math.max(0, Math.round(vv.offsetTop));
    root.style.setProperty("--kr-vh", `${height}px`);
    root.style.setProperty("--kr-vtop", `${top}px`);
    const full = max.get(Math.max(height, win.innerHeight));
    const field = isTextField(win.document.activeElement);
    const kb = field && (win.innerHeight - height > 100 || height < full * 0.8);
    root.classList.toggle("kr-kb", kb);
    // A phone on its side with the keyboard up has a strip of ~150–200 px left: the command column keeps only the
    // conversation and the bar there.
    root.classList.toggle("kr-tight", kb && height < 320);
    // Once per keyboard size and field: her own scrolling while she types is left alone.
    const key = kb ? `${height}:${top}` : "";
    const focusedNow = win.document.activeElement;
    if (kb && (key !== lastKey || focusedNow !== lastField)) {
      // The focused field stays in sight inside its own scroller (a panel, the sign-in card, the Edit slide-over).
      // "nearest" moves nothing when the field is already in sight in its scroller (and not under the dock beside it).
      // The surfaces clip rather than hide their overflow (runner.css), so this scrolls only the real scrollers.
      (win.document.activeElement as HTMLElement).scrollIntoView({ block: "nearest", inline: "nearest" });
    }
    if (wasKb && !kb && win.scrollY !== 0 && root.querySelector(".runner, .kr-auth, .kr-solo")) win.scrollTo(0, 0);
    wasKb = kb;
    lastKey = key;
    lastField = focusedNow;
  };
  const schedule = () => { if (!frame) frame = win.requestAnimationFrame(apply); };
  apply();
  vv?.addEventListener("resize", schedule);
  vv?.addEventListener("scroll", schedule);
  win.addEventListener("resize", schedule);
  win.addEventListener("orientationchange", schedule);
  win.document.addEventListener("focusin", schedule);
  win.document.addEventListener("focusout", schedule);
  return () => {
    vv?.removeEventListener("resize", schedule);
    vv?.removeEventListener("scroll", schedule);
    win.removeEventListener("resize", schedule);
    win.removeEventListener("orientationchange", schedule);
    win.document.removeEventListener("focusin", schedule);
    win.document.removeEventListener("focusout", schedule);
    if (frame) win.cancelAnimationFrame(frame);
  };
}
